import React from 'react';
import axios, { CancelTokenSource } from 'axios';
import { BlockProcedureExtResponse, TariffDto } from 'ts-types/api.types';
import { getAllTariffs } from 'services/tariffServices';
import { getAllBlockProcedures } from 'services/interventionServices';

interface State {
  tariffs: TariffDto[];
  blockProcedureMap: { [id: number] : BlockProcedureExtResponse };
  blockProcedures: BlockProcedureExtResponse[];
  referenceDataFetched: boolean;
  fetchReferenceData: (cancelTokenSource: CancelTokenSource) => void;
  getTariffById(tariffId: number) : TariffDto | undefined;
}

const InterventionUpsertRefDataContext = React.createContext({
  tariffs: [] as TariffDto[],
  blockProcedureMap: {} as { [id: number] : BlockProcedureExtResponse },
  blockProcedures: [] as BlockProcedureExtResponse[],
  referenceDataFetched: false,
  fetchReferenceData: (cancelTokenSource: CancelTokenSource) => {},
  getTariffById: (tariffId: number) : TariffDto | undefined => undefined
});

interface Props extends JSX.ElementChildrenAttribute {}

export class InterventionUpsertRefDataContextProvider extends React.Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  constructor(props: any) {
    super(props);

    this.state = {
      tariffs: [] as TariffDto[],
      blockProcedureMap: {} as { [id: number] : BlockProcedureExtResponse },
      blockProcedures: [] as BlockProcedureExtResponse[],
      referenceDataFetched: false,
      fetchReferenceData: this.fetchReferenceData,
      getTariffById: this.getTariffById
    };

    this.fetchReferenceData();
  }

  componentWillUnmount(): void {
    this.cancelTokenSource.cancel('Component unmounted.');
  }

  fetchReferenceData = () : void => {
    const {cancelTokenSource} = this;
    Promise.all([
          getAllTariffs(cancelTokenSource),
          getAllBlockProcedures(cancelTokenSource)
        ])
        .then(result => {

          const tariffs = result[0];
          const blockProcedureMap = result[1];

          this.setState({
            referenceDataFetched: true,
            tariffs: tariffs,
            blockProcedureMap: blockProcedureMap,
            blockProcedures: Object.values(blockProcedureMap)
          })
        });
  };

  getTariffById = (tariffId: number) : TariffDto | undefined => {
    const {tariffs} = this.state;
    const tariff = tariffs.find(tariff => tariff.id === tariffId);
    return tariff;
  }

  render() {
    return (
        <InterventionUpsertRefDataContext.Provider value={this.state}>
          {this.props.children}
        </InterventionUpsertRefDataContext.Provider>
    )
  }
}

export const InterventionUpsertRefDataConsumer = InterventionUpsertRefDataContext.Consumer;
export interface InterventionUpsertRefDataConsumerRenderProps extends State {}

export const withInterventionUpsertRefDataContext =
    (Component: typeof React.Component | React.FunctionComponent<any> ) => {
      return (props: any) => (
          <InterventionUpsertRefDataConsumer>
            {({...interventionUpsertProps}: InterventionUpsertRefDataConsumerRenderProps) => {
              return <Component {...props} {...interventionUpsertProps} />
            }}
          </InterventionUpsertRefDataConsumer>
      );
    };
